// What nventr connects to, in one record. The homepage "Works with" rows
// and the /works-with page both read it, so the two cannot drift.
//
// Every entry is a seam that exists in code today unless it carries
// `status`: 'progress' (the selector and catalog exist, the drivers are the
// next phase) or 'ask' — the page labels those, never lists them as
// supported.
// `via` names the mechanism when a system is reached through a general
// seam (REST, SMTP, SigV4) rather than a named connector; the page says
// so instead of implying a native integration.

export const WORKS_WITH_GROUPS = [
  {
    id: 'ww-clouds',
    group: 'Clouds and where it runs',
    intro: 'The managed host is included. The same stack runs in your account or on one VM, and the license moves with it.',
    short: 'Runs on',
    items: [
      { name: 'Amazon Web Services', body: 'The managed fleet and the self-host target: ECS Fargate, S3, SSM and Secrets Manager, CloudFront, CodeCommit, SES. Any other AWS API is a SigV4 integration.', tag: 'today' },
      { name: 'Google Cloud · Firebase', body: 'Firebase Auth, Firestore and Hosting are one of the two data planes; the plane itself runs on it.', tag: 'today' },
      { name: 'Supabase', body: 'Postgres, GoTrue auth and storage as the second data plane; the default on a self-host stamp.', tag: 'today' },
      { name: 'MinIO', body: 'S3-compatible object storage for self-host and air-gapped stamps, behind the same S3 endpoint seam.', tag: 'today' },
      { name: 'One-VM self-host', body: 'Docker Compose behind Caddy, split into a data tier and a blue/green app tier; a kit release lands as a swap.', tag: 'today' },
      { name: 'Microsoft Azure', body: 'A second place to run: App Kit instances on native Azure (Container Apps, Blob Storage, Key Vault, Container Registry, Log Analytics, Azure Database for PostgreSQL, Communication Services Email) or as sealed images on a VM in your subscription; hosted instances on Azure VMs from the plane. One switch, CLOUD_PROVIDER=azure, selects every driver.', tag: 'today' },
    ],
  },
  {
    id: 'ww-identity',
    group: 'Identity',
    intro: 'Sign in with the directory you already run; provision from it.',
    short: 'Identity',
    items: [
      { name: 'SAML 2.0 and OIDC', body: 'Per-organization single sign-on from Platform Business; “Continue with SSO” discovers the provider from the email domain, and SSO can be enforced for verified domains.', tag: 'today' },
      { name: 'Okta · Microsoft Entra ID · Google Workspace · OneLogin · Ping', body: 'Any SAML or OIDC identity provider; these are the ones the console names in its setup steps.', tag: 'today' },
      { name: 'SCIM 2.0', body: 'Users and Groups endpoints per organization with a bearer token, creating members and access grants from your directory. Enterprise.', tag: 'today' },
      { name: 'Sign in with nventr.io', body: 'A hosted App Kit instance trusts the platform: one login, the same organization role on every instance.', tag: 'today' },
    ],
  },
  {
    id: 'ww-knowledge',
    group: 'Knowledge sources',
    intro: 'Where the documents agents answer from come in. New material arrives as a draft; a person publishes it.',
    short: 'Knowledge',
    items: [
      { name: 'Microsoft SharePoint', body: 'Sites and document libraries, synced hourly, daily or weekly.', tag: 'today' },
      { name: 'Atlassian Confluence', body: 'Spaces and pages on the same schedule.', tag: 'today' },
      { name: 'Google Drive', body: 'Folders you share with the connector.', tag: 'today' },
      { name: 'Dropbox', body: 'A folder, recursively, through a scoped app — PDF, Word, text and Markdown.', tag: 'today' },
      { name: 'Notion', body: 'Pages and databases under a shared integration.', tag: 'today' },
      { name: 'Amazon S3', body: 'A bucket or prefix, in your account or ours.', tag: 'today' },
      { name: 'Website crawl', body: 'Your public site, crawled on the schedule you set.', tag: 'today' },
      { name: 'Files', body: 'PDF, Word, text, Markdown and CSV uploaded directly, including thousands at a time through a Dev Console session.', tag: 'today' },
      { name: 'OneDrive · Box · Google Cloud Storage', body: 'Not connectors today. Upload the files, point a crawl at a shared site, or tell us which one you need first.', tag: 'ask us', status: 'ask' },
    ],
  },
  {
    id: 'ww-systems',
    group: 'Systems a workflow writes into',
    intro: 'A side effect is a capability you declare on the pipeline. Undeclared means the call does not happen.',
    short: 'Systems',
    items: [
      { name: 'Any REST or JSON API', body: 'Declared once with its base URL and actions. Auth: none, bearer, API key, OAuth 2.0 client credentials, or AWS SigV4. NetSuite, Salesforce, HubSpot, Zendesk, Jira, QuickBooks and the rest are reached this way — there is no vendor SDK between you and the call.', tag: 'today' },
      { name: 'Outgoing webhooks', body: 'Signed POSTs to a URL you own, with delivery status on every event.', tag: 'today' },
      { name: 'Slack and Microsoft Teams', body: 'Incoming webhooks as notification integrations: gate held, run failed, invoice posted.', tag: 'today', via: 'webhook' },
      { name: 'Email', body: 'Any SMTP relay — Microsoft 365, Google Workspace, Amazon SES, or the mail server inside a self-host stamp. Never a vendor email SDK.', tag: 'today', via: 'SMTP' },
      { name: 'GraphQL on your own data', body: 'Every record type in your organization is a GraphQL type already; a workflow queries it as a block.', tag: 'today' },
      { name: 'AWS services by SigV4', body: 'Transcribe, Textract, Bedrock or any AWS API, signed with a key that never leaves the secret store.', tag: 'today' },
      { name: 'Cross-account S3', body: 'A bucket in another AWS account as an nQube S3 block source or sink.', tag: 'today' },
    ],
  },
  {
    id: 'ww-models',
    group: 'Models',
    intro: 'nventr smart routing by default; your own key for $0 in model credits; or the sovereign runner on your own endpoint.',
    short: 'Models',
    items: [
      { name: 'OpenAI · Anthropic · xAI · Google Gemini', body: 'The frontier tier; realtime voice from xAI and OpenAI.', tag: 'today' },
      { name: 'Groq · Together · OpenRouter · DeepSeek · Moonshot', body: 'The bulk and strong tiers the router escalates through.', tag: 'today' },
      { name: 'fal.ai — Flux, Kling, Runway, Luma', body: 'Image and video generation blocks.', tag: 'today' },
      { name: 'vLLM · Ollama', body: 'Your own endpoint, on your own hardware, as the local tier or the only tier.', tag: 'today' },
    ],
  },
  {
    id: 'ww-code',
    group: 'Code, agents and delivery',
    intro: 'Where the Dev Console works, and where a change goes.',
    short: 'Code',
    items: [
      { name: 'GitHub · Gitea · AWS CodeCommit', body: 'The layer repo, the mirror of an app you keep, and the remote reviewed batches are pushed to.', tag: 'today' },
      { name: 'Cursor cloud agents', body: 'The default Dev Console runtime; or a warm pool; or the sovereign runner on your own model endpoint.', tag: 'today' },
      { name: 'Docker', body: 'An app you keep with a Dockerfile is built and run as a container beside the console; your own pipeline otherwise.', tag: 'today' },
      { name: 'Tauri · Capacitor', body: 'Desktop and mobile shells around the same app, as the native bundles on pricing.', tag: 'today' },
      { name: 'MCP — Cursor, Claude, ChatGPT', body: 'Every instance is an MCP server; the same tools as the in-app assistant, under the member’s seat, audited.', tag: 'today' },
    ],
  },
];

// The homepage rows: group label → names only, supported entries only.
export const WORKS_WITH_ROWS = WORKS_WITH_GROUPS.map((g) => ({
  k: g.short,
  items: g.items.filter((it) => !it.status).map((it) => it.name),
}));
